import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CountdownComponent, CountdownConfig } from 'ngx-countdown';
import { ConfigModel } from 'src/app/config/config.model';
import { ConfigService } from 'src/app/config/config.service';
import { ParametrizacionService } from 'src/app/open-api';
import { AppToastService } from '../../services/app-toast.service';
import Swal from 'sweetalert2';
import { AuthService } from '../../services/auth.service';
import { UserModel } from '../../models/user.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-otp',
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss']
})
export class OtpComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() otpValue: EventEmitter<string> = new EventEmitter<string>();
  @Output() cancel: EventEmitter<string> = new EventEmitter<string>();
  @Output() otpInvalid: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  @Output() readyComponent: EventEmitter<boolean> = new EventEmitter<boolean>(false);
  
  @Input() set BeginCountDown(start: boolean) {
    this.StartCount(start);
  }

  @ViewChild('cd', { static: false }) countDown?: CountdownComponent;
  @ViewChild('hiddenInput') hiddenInput?: ElementRef<HTMLInputElement>;

  config: ConfigModel = {};
  user: UserModel = {};
  formOtp: FormGroup;
  keyboard: number[] = [];
  configCD: CountdownConfig = {
    demand: true,
    leftTime: 6000
  };
  
  caract = 6;
  intAct = 0; 
  maxInt = 0; 
  private userSub?: Subscription;

  constructor(
    private configService: ConfigService,
    private parametrizacionService: ParametrizacionService,
    private toastService: AppToastService,
    private fb: FormBuilder,
    private router: Router,
    private modalService: NgbModal,
    private authService: AuthService
  ) {
    this.formOtp = this.fb.group({
      otp: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  async ngOnInit(): Promise<void> {
    this.userSub = this.authService.user.subscribe(user => {
      this.user = user;
      if (this.isClaveDigital()) {
        setTimeout(() => this.focusHiddenInput(), 500);
      }
    });
    
    this.GenerateKeyboard();
    this.ObtenerConfiguracion();
    await this.ObtenerParametrizacionOtp();
  }

  ngAfterViewInit() {
    if (this.isClaveDigital()) {
      this.focusHiddenInput();
    }
  }

  ngOnDestroy() {
    if (this.userSub) this.userSub.unsubscribe();
  }

  isClaveDigital(): boolean {
    return !!(this.user?.usaClaveDigital && this.user?.dispositivo);
  }

  focusHiddenInput() {
    if (this.hiddenInput) {
      this.hiddenInput.nativeElement.focus();
    }
  }

  onPhysicalInput(event: any) {
    const input = event.target as HTMLInputElement;
    let cleanValue = input.value.replace(/[^0-9]/g, '');
    
    if (cleanValue.length > this.caract) {
      cleanValue = cleanValue.substring(0, this.caract);
    }

    this.formOtp.get('otp')?.setValue(cleanValue);
    input.value = cleanValue; 
  }

  GetOtpValue(): AbstractControl | null {
    return this.formOtp.get('otp');
  }

  WriteOtp(value: number): void {
    let otp: string = this.GetOtpValue()?.value || '';
    if (otp.length < this.caract) {
      otp = otp + value;
    }
    this.GetOtpValue()?.setValue(otp);
  }

  DeleteOtp(): void {
    let otp: string = this.GetOtpValue()?.value || '';
    if (otp.length > 0) {
      otp = otp.slice(0, -1);
    }
    this.GetOtpValue()?.setValue(otp);
  }

  ObtenerConfiguracion(): void {
    this.configService.config$.subscribe(config => {
      this.config = config;
    });
  }

  async ObtenerParametrizacionOtp(): Promise<void> {
    try {
      const parametrizacion = await this.parametrizacionService
        .apiV10ParametrizacionObtenerParametrizacionOtpGet()
        .toPromise();

      this.caract = parseInt(parametrizacion.lista?.find(p =>
        p.id === this.config.Parametrizacion?.Otp?.NumeroCaracteresId)?.valor || '6', 10);

      if (this.isClaveDigital()) {
        this.maxInt = parseInt(parametrizacion.lista?.find(p =>
          p.id === this.config.Parametrizacion?.Otp?.IntentosFallidosId)?.valor || '3', 10); 
        this.intAct = this.maxInt;
        this.configCD = null as any;
      } else {
        this.maxInt = parseInt(parametrizacion.lista?.find(p =>
          p.id === this.config.Parametrizacion?.Otp?.IntentosFallidosId)?.valor || '3', 10);
        this.intAct = this.maxInt;
        
        const minutos = parseInt(parametrizacion.lista?.find(p =>
          p.id === this.config.Parametrizacion?.Otp?.TiempoVidaId)?.valor || '5', 10);

        this.configCD = {
          demand: true,
          leftTime: 60 * minutos,
          format: 'HH:mm:ss'
        };
      }

      this.formOtp = this.fb.group({
        otp: ['', [Validators.required, Validators.minLength(this.caract)]]
      });

    } catch (error) {
      console.error("Error en parametrización", error);
    }
  }

  EmittOtpValue(): void {
    const value = this.formOtp.get('otp')?.value;
    this.otpValue.emit(value);
    this.resetFormState();
  }

  resetFormState() {
    this.formOtp.get('otp')?.setValue('');
    if (this.hiddenInput) {
      this.hiddenInput.nativeElement.value = '';
    }
  }

  EmittCancel() {
    Swal.fire({
      title: 'Cancelar Verificación',
      text: '¿Está seguro que desea cancelar la verificación?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3b7851',
      cancelButtonText: 'Cancelar',
      cancelButtonColor: "#d33"
    }).then((result) => {
      if (result.isConfirmed) {
        this.cancel.emit('Cancelar');
      }
    });
  }

  GenerateKeyboard(): void {
    this.keyboard = [];
    while (this.keyboard.length < 10) {
      const key = Math.floor(Math.random() * 10);
      if (this.keyboard.indexOf(key) === -1) {
        this.keyboard.push(key);
      }
    }
  }

  StartCount(start: boolean): void {
    if (start) this.countDown?.begin();
  }

  TimeOut(event: any): void {
    if (event?.action === 'restart') {
      this.readyComponent.emit(true);
    }
    if (event?.status === 3) {
      this.toastService.show('Error: Se ha agotado el tiempo para el código de verificación.', 'bg-danger text-light');
      this.otpInvalid.emit(true);
      
      setTimeout(() => {
        this.router.navigate(['login']);
      }, 1500);
    }
  }

 Decrease(): void {
  this.intAct -= 1;
  this.resetFormState(); 

  if (this.intAct <= 0) {
    this.otpInvalid.emit(true);
    const msg = 'Se han agotado los intentos permitidos.';
    this.toastService.show(msg, 'bg-danger text-light');
    
    setTimeout(() => {
      this.router.navigate(['login']);
    }, 1500);

  } else {
    this.toastService.show(
      `Código incorrecto. Le quedan ${this.intAct} intentos.`,
      'bg-warning text-dark'
    );

    if (this.isClaveDigital()) {
      setTimeout(() => this.focusHiddenInput(), 100);
    }
  }
}
}